/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useEffect } from 'react';
import { useRouter, useQueryParam, useRouterActions } from '@atlassian/react-resource-router';
import FullPageRedirect from '@atlassian/jira-common-navigation/src/full-page-redirect/index.tsx';

export const InlineRedirect: {
	({ to }: { to: string }): React.JSX.Element;
	displayName: string;
} = ({ to }: { to: string }): React.JSX.Element => (
	<script
		// eslint-disable-next-line react/no-danger
		dangerouslySetInnerHTML={{
			__html: `window.location.assign("${to}")`,
		}}
	/>
);
InlineRedirect.displayName = 'InlineRedirect';

/**
 * Facilitates user redirection to the login page, with special handling for embedded pages and specific scenarios to ensure a smooth post-login experience. It constructs the appropriate redirect URL based on the current and target pages, taking into account special cases such as embedded issue creation.
 */
export const RedirectToLoginPage = (): React.JSX.Element => {
	const [
		{
			location: { pathname, search },
		},
	] = useRouter();
	const redirectUrl = pathname + search;
	const encodedRedirectUrl = encodeURIComponent(redirectUrl);
	let path = `/login.jsp?os_destination=${encodedRedirectUrl}`;
	const parentProductQueryName = 'parentProduct';
	const [parentProduct] = useQueryParam(parentProductQueryName);
	if (pathname.endsWith('/embed')) {
		const searchParams = new URLSearchParams(search);
		// We need to remove parentProduct param as it is only used in embed urls
		searchParams.delete(parentProductQueryName);
		/**
		 * encodedLink is the link to the non embedded version of the embed url.
		 * So, we strip out the /embed suffix from path
		 */
		let link = `${pathname.slice(0, -6)}?${searchParams}`;
		/**
		 * this is the specific case for create issue embed, where we need to redirect
		 * user to your-work page with create issue as true to open GIC by default.
		 * We will remove this when we FPIC exp implemented for GIC
		 */
		if (pathname.includes('/issues/create')) {
			link = '/jira/your-work?createissue=true';
		}

		const loginEmbedQueryParams = new URLSearchParams([
			['destination', redirectUrl],
			['link', link],
			['parentProduct', parentProduct || ''],
		]);
		path = `/jira-login/embed?${loginEmbedQueryParams}`;
		// Embeds are generally served inside iframes, and that means our frame-ancestors
		// CSP policy can cause issues if we perform a full page reload when on a custom domain
		// Namely, the referrer for the login page (used to derive dynamic CSP) will be the page
		// triggering the redirection, instead of the custom domain that embedded the iframe.
		return <ClientSideRedirect url={path} />;
	}

	return <FullPageRedirect url={path} />;
};

function ClientSideRedirect({ url }: { url: string }) {
	const { push } = useRouterActions();

	useEffect(() => {
		push(url);
	}, [push, url]);

	return null;
}
